import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface HeroProps {
  onReserveClick: () => void;
}

export default function Hero({ onReserveClick }: HeroProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleReserveClick = () => {
    setMenuOpen(false);
    onReserveClick();
  };

  return (
    <div className="relative min-h-screen flex flex-col">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || menuOpen ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className={`max-w-7xl mx-auto px-6 flex items-center justify-between transition-all duration-300 ${isScrolled ? 'py-2' : 'py-4'}`}>
          <div className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/0cdc8a6b92ed12030230ca2c206db970.png"
              alt="BoxFreeMove"
              className={`w-auto transition-all duration-300 ${isScrolled ? 'h-12' : 'h-28'}`}
              style={{ filter: isScrolled || menuOpen ? 'none' : 'brightness(0) invert(1)' }}
            />
          </div>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#how-it-works" className={`text-sm font-medium transition-colors hover:text-yellow-400 ${isScrolled ? 'text-gray-800' : 'text-white'}`}>How It Works</a>
            <a href="#pricing" className={`text-sm font-medium transition-colors hover:text-yellow-400 ${isScrolled ? 'text-gray-800' : 'text-white'}`}>Pricing</a>
            <a href="#faq" className={`text-sm font-medium transition-colors hover:text-yellow-400 ${isScrolled ? 'text-gray-800' : 'text-white'}`}>FAQ</a>
            <Link to="/moving-tips" className={`text-sm font-medium transition-colors hover:text-yellow-400 ${isScrolled ? 'text-gray-800' : 'text-white'}`}>Moving Tips</Link>
            <button onClick={onReserveClick} className="bg-yellow-400 text-gray-900 px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer">
              Reserve Now
            </button>
          </div>

          {/* Hamburger button (mobile only) */}
          <button
            className="md:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 transition-all duration-300 ${isScrolled || menuOpen ? 'bg-gray-800' : 'bg-white'} ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`block w-6 h-0.5 transition-all duration-300 ${isScrolled || menuOpen ? 'bg-gray-800' : 'bg-white'} ${menuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`block w-6 h-0.5 transition-all duration-300 ${isScrolled || menuOpen ? 'bg-gray-800' : 'bg-white'} ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </button>
        </div>

        {/* Mobile dropdown menu */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${menuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
            <a
              href="#how-it-works"
              onClick={() => handleNavClick('#how-it-works')}
              className="text-gray-800 font-medium text-sm hover:text-yellow-500 transition-colors cursor-pointer"
            >
              How It Works
            </a>
            <a
              href="#pricing"
              onClick={() => handleNavClick('#pricing')}
              className="text-gray-800 font-medium text-sm hover:text-yellow-500 transition-colors cursor-pointer"
            >
              Pricing
            </a>
            <a
              href="#faq"
              onClick={() => handleNavClick('#faq')}
              className="text-gray-800 font-medium text-sm hover:text-yellow-500 transition-colors cursor-pointer"
            >
              FAQ
            </a>
            <Link
              to="/moving-tips"
              onClick={() => setMenuOpen(false)}
              className="text-gray-800 font-medium text-sm hover:text-yellow-500 transition-colors cursor-pointer"
            >
              Moving Tips
            </Link>
            <button
              onClick={handleReserveClick}
              className="bg-yellow-400 text-gray-900 px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer w-full"
            >
              Reserve Now
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative flex-1 flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-28 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Moving? Don't Buy Boxes You'll Just Have to Throw Away.
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
            Move smarter with sturdy, stackable, weather-proof totes delivered right to your door and hauled away when you&apos;re done.
          </p>

          <div className="max-w-2xl mx-auto mb-10">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-left">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1">No cardboard.</h3>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1">No tape.</h3>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1">No box cleanup.</h3>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1">No weather worries.</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Images */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-10">
            <div className="relative rounded-2xl overflow-hidden w-full bg-black/20">
              <img
                src="https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/59ef3305d1556c3bc22a6bf335ea2edd.png"
                alt="Disorganized cardboard boxes in a moving truck"
                className="w-full h-auto object-contain"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                <span className="inline-block bg-red-500/90 text-white text-xs font-bold px-3 py-1 rounded-full mb-2 uppercase tracking-wide">The Old Way</span>
                <p className="text-white font-semibold text-sm">Cardboard chaos, tape everywhere, total mess.</p>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden w-full bg-black/20">
              <img
                src="https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/10d0fabe1d6d9062e6dd99e83c2dc89a.png"
                alt="Neatly stacked BoxFreeMove totes in a moving truck"
                className="w-full h-auto object-contain"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                <span className="inline-block bg-yellow-400/90 text-gray-900 text-xs font-bold px-3 py-1 rounded-full mb-2 uppercase tracking-wide">The BoxFreeMove Way</span>
                <p className="text-white font-semibold text-sm">Clean totes delivered. No tape. No stress.</p>
              </div>
            </div>
          </div>

          <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
            We deliver durable moving totes before your move and pick them up when you're done.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={onReserveClick} className="bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer">
              Reserve Your Totes
            </button>
            <button onClick={scrollToPricing} className="bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer">
              See Pricing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
